import { Role, isRole } from '@worklogix/shared';
import { useAuth } from '@worklogix/web-common';

/**
 * Extract role strings from an Entra id-token `roles` claim without assuming a
 * concrete claim type (MSAL types it loosely). Returns `undefined` when the
 * claim is absent — treated as "unknown", not "no roles".
 */
function readRoleClaim(claims: unknown): string[] | undefined {
  if (typeof claims !== 'object' || claims === null || !('roles' in claims)) {
    return undefined;
  }
  const roles = (claims as { roles?: unknown }).roles;
  if (!Array.isArray(roles)) {
    return undefined;
  }
  return roles.filter((r): r is string => typeof r === 'string');
}

/**
 * True when the signed-in user carries the Admin app role.
 *
 * This gates ADMIN-ONLY UI affordances (the Hours scope toggle, the Employee ↔
 * NS + Supervisor mapping tab). Unlike {@link import('./useIsManager').useIsManager},
 * which is deliberately fail-OPEN, admin detection is fail-CLOSED: when the
 * `roles` claim is absent or the user is not positively an Admin we do NOT show
 * admin surfaces. The API remains the authoritative gate (Admin-only endpoints
 * 403 regardless); this only decides what to render.
 */
export function useIsAdmin(): boolean {
  const { account } = useAuth();
  const roles = readRoleClaim(account?.idTokenClaims);

  if (roles === undefined) {
    return false;
  }

  return roles.some((r) => isRole(r) && r === Role.Admin);
}
